import { MutableRefObject, useCallback, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { clamp } from 'lodash';
import { GridApi, IDatasource, IGetRowsParams } from 'ag-grid-community';

import {
  normalizePageIndex,
  parsePageIndex,
} from 'components/pages/ContentManagement/Creatives/Content/Creatives/utils';
import { isValidIndex } from 'utils/numberUtils';

interface UseNavigateTable {
  pageIndex: number;
  refreshTable: () => void;
  wirePaginationToSearchParams: (tableApi: GridApi) => void;
}

interface UseNavigateTableProps<T> {
  tableApiRef: MutableRefObject<GridApi | null>;
  pageSize: number;
  pageParamKey?: string;
  fetchCreativesTable: ({
    params,
    pageIndex,
  }: {
    params: IGetRowsParams;
    pageIndex: number;
  }) => Promise<{ content: T[]; totalElements: number }>;
  reApplyBulkSelection?: (tableApi: GridApi | null) => void;
}

export const useNavigateTable = <T>({
  tableApiRef,
  pageSize,
  pageParamKey = 'page',
  fetchCreativesTable,
  reApplyBulkSelection,
}: UseNavigateTableProps<T>): UseNavigateTable => {
  const [searchParams, setSearchParams] = useSearchParams();
  const pageIndex = parsePageIndex(searchParams.get(pageParamKey));
  const pageIndexRef = useRef(pageIndex);
  pageIndexRef.current = pageIndex;

  const handleNormalizePageFormat = useCallback(
    (page: number): void => {
      setSearchParams(
        (prev) => {
          prev.set(pageParamKey, normalizePageIndex(page).toString());
          return prev;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const refreshTable = (): void => {
    const getDataSource = (): IDatasource => ({
      getRows: async (params: IGetRowsParams): Promise<void> => {
        const calculatedPage = params.startRow / pageSize;

        if (!isValidIndex(pageIndexRef.current)) {
          handleNormalizePageFormat(calculatedPage);
          pageIndexRef.current = calculatedPage;
        }

        try {
          const { content, totalElements } = await fetchCreativesTable({ params, pageIndex: pageIndexRef.current });

          if (totalElements === 0) {
            tableApiRef.current?.showNoRowsOverlay();
          } else {
            tableApiRef.current?.hideOverlay();
          }

          params.successCallback(content, totalElements);

          if (pageIndexRef.current > 0 && params.startRow === 0) {
            tableApiRef.current?.paginationGoToPage(pageIndexRef.current);
          }
        } catch {} // eslint-disable-line no-empty

        reApplyBulkSelection?.(tableApiRef.current);
      },
    });

    if (tableApiRef.current) {
      tableApiRef.current.setDatasource(getDataSource());
    }
  };

  const wirePaginationToSearchParams = useCallback(
    (tableApi: GridApi): void => {
      if (!tableApi.paginationGoToFirstPage) return;

      tableApi.paginationGoToFirstPage = () => handleNormalizePageFormat(0);

      tableApi.paginationGoToLastPage = () => {
        const total = tableApi.paginationGetTotalPages();
        handleNormalizePageFormat(total - 1);
      };

      tableApi.paginationGoToNextPage = () => {
        const current = isValidIndex(pageIndexRef.current) ? pageIndexRef.current : tableApi.paginationGetCurrentPage();
        const total = tableApi.paginationGetTotalPages();
        handleNormalizePageFormat(clamp(current + 1, 0, total - 1));
      };

      tableApi.paginationGoToPreviousPage = () => {
        const current = isValidIndex(pageIndexRef.current) ? pageIndexRef.current : tableApi.paginationGetCurrentPage();
        const total = tableApi.paginationGetTotalPages();
        handleNormalizePageFormat(clamp(current - 1, 0, total - 1));
      };

      if (isValidIndex(pageIndexRef.current)) {
        tableApi.paginationGoToPage(pageIndexRef.current);
      }
    },
    [handleNormalizePageFormat],
  );

  useEffect(() => {
    if (isValidIndex(pageIndex) && tableApiRef.current?.paginationGoToPage) {
      const currentGridPage = tableApiRef.current.paginationGetCurrentPage();
      if (currentGridPage === pageIndex) {
        refreshTable();
      } else {
        tableApiRef.current.paginationGoToPage(pageIndex);
      }
    }
  }, [pageIndex]);

  return {
    pageIndex,
    refreshTable,
    wirePaginationToSearchParams,
  };
};
